import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ApiService, ApiResponse } from './api.service';
import { LanguageService } from './language.service';
import { FinanceService } from './finance.service';

export interface Permission {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  group?: string | null;
}

export interface PermissionProfile {
  id: number;
  name: string;
  description?: string;
  permissions: Permission[];
}

export interface User {
  id: number;
  name: string;
  email: string;
  locale?: string;
  default_module?: string;
  pending_email?: string;
  roles?: string[];
  is_admin?: boolean;
  is_superadmin?: boolean;
  permissions?: Permission[];
  permission_profile?: PermissionProfile | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface ApiToken {
  id: number;
  name: string;
  last_used: string | null;
  created_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private languageService = inject(LanguageService);
  private financeService = inject(FinanceService);

  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;

  private isAuthenticatedSubject: BehaviorSubject<boolean>;
  public isAuthenticated$: Observable<boolean>;

  constructor(private apiService: ApiService) {
    const storedUser = this.getUserFromStorage();
    this.currentUserSubject = new BehaviorSubject<User | null>(storedUser);
    this.currentUser$ = this.currentUserSubject.asObservable();

    const hasToken = !!localStorage.getItem('auth_token');
    this.isAuthenticatedSubject = new BehaviorSubject<boolean>(hasToken);
    this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

    if (hasToken) {
      this.loadUser();
    }
  }

  /**
   * Register a new user
   */
  register(data: RegisterData): Observable<ApiResponse> {
    return this.apiService.post('register', data);
  }

  /**
   * Login user
   */
  login(credentials: LoginCredentials): Observable<ApiResponse> {
    return this.apiService.post('login', credentials).pipe(
      tap(response => {
        if (response.data?.access_token && response.data?.user) {
          this.handleAuthResponse(response.data);
        }
      })
    );
  }

  /**
   * Logout user
   */
  logout(): Observable<ApiResponse> {
    return this.apiService.post('logout', {}).pipe(
      tap(() => this.clearAuth())
    );
  }

  /**
   * Get current user from server
   */
  loadUser(): void {
    this.apiService.get<{ user: User }>('user').subscribe({
      next: (response) => {
        const user = response.data?.user;
        if (user) {
          this.currentUserSubject.next(user);
          this.saveUserToStorage(user);
          this.isAuthenticatedSubject.next(true);
          this.languageService.setUserLanguage(user.locale);
        }
      },
      error: () => {
        this.clearAuth();
      }
    });
  }

  /**
   * Refresh authentication token
   */
  refreshToken(): Observable<ApiResponse> {
    return this.apiService.post('refresh', {}).pipe(
      tap(response => {
        if (response.data?.access_token) {
          this.apiService.setAuthToken(response.data.access_token);
        }
      })
    );
  }

  /**
   * Verify email address
   */
  verifyEmail(userId: string, token: string): Observable<ApiResponse> {
    const url = `email/verify/${userId}?token=${token}`;
    return this.apiService.get(url);
  }

  /**
   * Resend verification email
   */
  resendVerification(email: string): Observable<ApiResponse> {
    return this.apiService.post('email/resend', { email });
  }

  /**
   * Request password reset
   */
  requestPasswordReset(email: string): Observable<ApiResponse> {
    return this.apiService.post('password/request', { email });
  }

  /**
   * Reset password with token
   */
  resetPassword(userId: string, token: string, password: string, passwordConfirmation: string): Observable<ApiResponse> {
    return this.apiService.post(`password/reset/${userId}`, {
      token,
      password,
      password_confirmation: passwordConfirmation
    });
  }

  /**
   * Change password for authenticated user
   */
  changePassword(currentPassword: string, newPassword: string, newPasswordConfirmation: string): Observable<ApiResponse> {
    return this.apiService.post('password/change', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation
    }).pipe(
      tap(() => {
        this.clearAuth();
      })
    );
  }

  /**
   * Get all active sessions for the current user
   */
  getSessions(): Observable<ApiResponse> {
    return this.apiService.get('sessions');
  }

  /**
   * Revoke a specific session
   */
  revokeSession(sessionId: number): Observable<ApiResponse> {
    return this.apiService.delete(`sessions/${sessionId}`);
  }

  /**
   * Revoke all sessions except the current one
   */
  revokeAllSessions(): Observable<ApiResponse> {
    return this.apiService.delete('sessions');
  }

  /**
   * Get security logs for the current user
   */
  getSecurityLogs(): Observable<ApiResponse> {
    return this.apiService.get('security-logs');
  }

  /**
   * Request email change with password confirmation
   */
  requestEmailChange(newEmail: string, password: string): Observable<ApiResponse> {
    return this.apiService.post('email/change/request', {
      new_email: newEmail,
      password: password
    });
  }

  /**
   * Update user profile
   */
  updateProfile(data: { name?: string; locale?: string; default_module?: string }): Observable<ApiResponse> {
    return this.apiService.put('user/profile', data).pipe(
      tap(response => {
        if (response.data?.user) {
          const currentUser = this.currentUser;
          if (currentUser) {
            const updatedUser = { ...currentUser, ...response.data.user };
            this.currentUserSubject.next(updatedUser);
            this.saveUserToStorage(updatedUser);
          }
        }
      })
    );
  }

  /**
   * Cancel pending email change
   */
  cancelEmailChange(): Observable<ApiResponse> {
    return this.apiService.post('email/change/cancel', {});
  }

  /**
   * Verify email change with token
   */
  verifyEmailChange(userId: string, token: string): Observable<ApiResponse> {
    return this.apiService.post(`email/change/verify/${userId}`, {
      token: token
    });
  }

  // API Tokens
  getApiTokens(): Observable<ApiResponse<{ tokens: ApiToken[] }>> {
    return this.apiService.get('tokens');
  }

  createApiToken(name: string): Observable<ApiResponse<{ token: string; id: number; name: string; created_at: string }>> {
    return this.apiService.post('tokens', { name });
  }

  deleteApiToken(id: number): Observable<ApiResponse> {
    return this.apiService.delete(`tokens/${id}`);
  }

  /**
   * Get current user value
   */
  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasToken();
  }

  /**
   * Check if current user is super admin
   */
  isSuperAdmin(): boolean {
    return this.currentUser?.is_superadmin === true;
  }

  /**
   * Check if current user is admin (includes superadmin)
   */
  isAdmin(): boolean {
    return this.currentUser?.is_admin === true;
  }

  /**
   * Handle authentication response
   */
  handleAuthResponse(data: { access_token?: string; user?: User }): void {
    if (data.access_token && data.user) {
      this.apiService.setAuthToken(data.access_token);
      this.currentUserSubject.next(data.user);
      this.saveUserToStorage(data.user);
      this.isAuthenticatedSubject.next(true);
      this.languageService.setUserLanguage(data.user.locale);
    }
  }

  /**
   * Clear authentication data
   */
  private clearAuth(): void {
    this.apiService.removeAuthToken();
    this.currentUserSubject.next(null);
    this.removeUserFromStorage();
    this.isAuthenticatedSubject.next(false);
    this.financeService.resetCache();
  }

  /**
   * Check if auth token exists
   */
  private hasToken(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  /**
   * Save user to localStorage — only persist identity fields, not authorization data.
   * Roles, permissions, and admin flags are reloaded from the server on every app init.
   */
  private saveUserToStorage(user: User): void {
    const { id, name, email, locale, default_module } = user;
    localStorage.setItem('user', JSON.stringify({ id, name, email, locale, default_module }));
  }

  /**
   * Get user from localStorage
   */
  private getUserFromStorage(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Remove user from localStorage
   */
  private removeUserFromStorage(): void {
    localStorage.removeItem('user');
  }
}
